import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { Map as ImmutableMap } from 'immutable';

import { preferencesSelectors } from 'app-components/flux/preferences';
import { profileSelectors } from 'app-components/flux/profile';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';

import { showOnboardAccountsDialog } from 'components/Dialogs/A2A/actions';
import type { onboardA2ADialogIndex } from 'components/Dialogs/A2A/onboardA2ADialogIndexEnums';

import { DIALOG_TYPE_EMAIL_VERIFICATION_DIALOG } from 'components/Dialogs/EmailVerification';
import { emailDialogIndexEnums } from 'components/Dialogs/EmailVerification/emailDialogIndexEnums';

import { DIALOG_TYPE_IDENTITY_VERIFICATION_DIALOG } from 'components/Dialogs/IdentityVerification';
import { identityDialogIndexEnums } from 'components/Dialogs/IdentityVerification/identityDialogIndexEnums';

import { DIALOG_TYPE_PHONE_VERIFICATION_DIALOG } from 'components/Dialogs/PhoneVerification';
import { phoneDialogIndexEnums } from 'components/Dialogs/PhoneVerification/phoneDialogIndexEnums';

export default function useKickoffA2AFlow() {
  const dispatch = useAppDispatch();

  const identityVerified = useAppSelector(profileSelectors.getIdentityVerified);
  const emailVerified = useAppSelector(profileSelectors.getEmailVerified);
  const phoneVerified = useAppSelector(profileSelectors.getPhoneVerified);
  const preferences = useAppSelector((state) =>
    preferencesSelectors.getSharedPreferencesByPath(state, { group: 'user' }),
  );

  const fullyVerified = (props: { initiator?: string }) =>
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'a2a-identity-verification',
          type: DIALOG_TYPE_IDENTITY_VERIFICATION_DIALOG,
          allowNesting: true,
          props: ImmutableMap({
            startTabIndex: identityDialogIndexEnums.SUBMIT,
            initiator: props?.initiator || null,
          }),
        }),
      ),
    );

  const dispatchEmailOrPhone = (
    props: { initiator?: string },
    verifiedItem: string[] = [],
    skipOpeningPage?: boolean,
  ) => {
    if (!verifiedItem.includes('phone') && !phoneVerified) {
      dispatch(
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: 'a2a-phone-verification',
            type: DIALOG_TYPE_PHONE_VERIFICATION_DIALOG,
            allowNesting: true,
            props: ImmutableMap({
              startTab: skipOpeningPage ? phoneDialogIndexEnums.SUBMIT : phoneDialogIndexEnums.OPENING,
              initiator: props?.initiator || null,
              onConfirm: () => {
                verifiedItem.push('phone');
                dispatchEmailOrPhone({ initiator: 'phone verification' }, verifiedItem, true);
              },
            }),
          }),
        ),
      );
    } else if (!verifiedItem.includes('email') && !emailVerified) {
      // emailVerified will not update immediately, so we use the verifiedItem list
      dispatch(
        // to help with tracking!
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: 'a2a-email-verification',
            type: DIALOG_TYPE_EMAIL_VERIFICATION_DIALOG,
            allowNesting: true,
            props: ImmutableMap({
              startTab: skipOpeningPage ? emailDialogIndexEnums.SUBMIT : emailDialogIndexEnums.OPENING,
              initiator: props?.initiator || null,
              onConfirm: () => {
                verifiedItem.push('email');
                dispatchEmailOrPhone({ initiator: 'email verification' }, verifiedItem, true);
              },
            }),
          }),
        ),
      );
    } else {
      fullyVerified(props);
    }
  };

  const kickoffFlow = (props: {
    initiator?: string;
    startTabIndex?: onboardA2ADialogIndex;
    initialInstitutionId?: string;
    initialAccountId?: string;
  }) => {
    const forceIdentityReadyView = preferences && preferences.a2a && preferences.a2a.showIdentityReviewedCard;

    if (identityVerified && emailVerified && phoneVerified && !forceIdentityReadyView) {
      // already has a valid identity
      dispatch(
        showOnboardAccountsDialog({
          initiator: props?.initiator,
          startTabIndex: props?.startTabIndex,
          initialInstitutionId: props?.initialInstitutionId,
          initialAccountId: props?.initialAccountId,
        }),
      );
    } else if ((emailVerified && phoneVerified) || forceIdentityReadyView) {
      // no identity let's make one
      dispatch(
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: 'a2a-identity-verification',
            type: DIALOG_TYPE_IDENTITY_VERIFICATION_DIALOG,
            allowNesting: true,
            props: ImmutableMap({
              initiator: props?.initiator || null,
            }),
          }),
        ),
      );
    } else if (!emailVerified || !phoneVerified) {
      dispatchEmailOrPhone(props, []);
    }
  };

  return kickoffFlow;
}
